import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, AlertTriangle, Loader2, Search, X, ChevronDown, ChevronUp, Filter, DollarSign } from 'lucide-react';
import LogoutButton from '../../components/common/LogoutButton';
import {
  getSlideDevRiskRecords,
  getPlanDevRiskRecords,
  getSlideTokenStats,
  type RiskChatRecord,
  type RiskChatRecordsParams,
  type SlideTokenStatsItem
} from '../../lib/apis_chatmsg';

type RecordType = 'slide' | 'plan' | 'slideStats';

const formatDateTime = (dateStr: string): string => {
  const date = new Date(dateStr);
  return date.toLocaleString('zh-CN', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit'
  });
};

const formatTokens = (tokens: number): string => {
  if (tokens >= 1000000) {
    return `${(tokens / 1000000).toFixed(2)}M`;
  } else if (tokens >= 1000) {
    return `${(tokens / 1000).toFixed(1)}K`;
  }
  return tokens.toString();
};

const getTodayStart = (): string => {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  return `${year}-${month}-${day} 00:00:00`;
};

const getCurrentTime = (): string => {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  const hour = String(now.getHours()).padStart(2, '0');
  const minute = String(now.getMinutes()).padStart(2, '0');
  const second = String(now.getSeconds()).padStart(2, '0');
  return `${year}-${month}-${day} ${hour}:${minute}:${second}`;
};

const formatDateTimeForInput = (dateStr: string): string => {
  return dateStr.replace(' ', 'T').slice(0, 16);
};

const UsageWarningPage: React.FC = () => {
  const navigate = useNavigate();
  const [recordType, setRecordType] = useState<RecordType>('slide');
  const [isLoading, setIsLoading] = useState(false);
  const [records, setRecords] = useState<RiskChatRecord[]>([]);
  const [slideStatsRecords, setSlideStatsRecords] = useState<SlideTokenStatsItem[]>([]);
  const [total, setTotal] = useState(0);
  const [hasSearched, setHasSearched] = useState(false);
  const [expandedId, setExpandedId] = useState<number | null>(null);

  const [filterValues, setFilterValues] = useState({
    startTime: getTodayStart(),
    endTime: getCurrentTime(),
    minTokens: 170000,
    maxTokens: 400000,
    limit: 100
  });

  const [slideStatsLimit, setSlideStatsLimit] = useState(100);

  useEffect(() => {
    const timer = setInterval(() => {
      setFilterValues(prev => ({
        ...prev,
        endTime: getCurrentTime()
      }));
    }, 60000);

    return () => clearInterval(timer);
  }, []);

  const handleSearch = async () => {
    setIsLoading(true);
    setHasSearched(true);
    setExpandedId(null);

    try {
      if (recordType === 'slideStats') {
        const response = await getSlideTokenStats({ limit: slideStatsLimit });
        setSlideStatsRecords(response.items);
        setTotal(response.total);
      } else {
        const params: RiskChatRecordsParams = {
          start_time: filterValues.startTime,
          end_time: filterValues.endTime,
          min_tokens: filterValues.minTokens,
          max_tokens: filterValues.maxTokens,
          limit: filterValues.limit
        };

        const fetchFn = recordType === 'slide' ? getSlideDevRiskRecords : getPlanDevRiskRecords;
        const response = await fetchFn(params);
        setRecords(response.items);
        setTotal(response.total);
      }
    } catch (error) {
      console.error('Failed to fetch records:', error);
      setRecords([]);
      setSlideStatsRecords([]);
      setTotal(0);
    } finally {
      setIsLoading(false);
    }
  };

  const handleReset = () => {
    if (recordType === 'slideStats') {
      setSlideStatsLimit(100);
    } else {
      setFilterValues({
        startTime: getTodayStart(),
        endTime: getCurrentTime(),
        minTokens: 170000,
        maxTokens: 400000,
        limit: 100
      });
    }
    setRecords([]);
    setSlideStatsRecords([]);
    setTotal(0);
    setHasSearched(false);
    setExpandedId(null);
  };

  const toggleExpand = (id: number) => {
    setExpandedId(expandedId === id ? null : id);
  };

  const handleRecordTypeChange = (type: RecordType) => {
    setRecordType(type);
    setRecords([]);
    setSlideStatsRecords([]);
    setHasSearched(false);
    setExpandedId(null);
  };

  const renderFilterForm = () => {
    if (recordType === 'slideStats') {
      return (
        <div className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm text-gray-600 mb-1">返回行数</label>
              <input
                type="number"
                value={slideStatsLimit}
                onChange={(e) => setSlideStatsLimit(Number(e.target.value) || 100)}
                min={1}
                max={1000}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-500 text-sm"
              />
            </div>
          </div>

          <div className="flex items-center gap-3">
            <button
              onClick={handleSearch}
              disabled={isLoading}
              className="flex items-center gap-2 px-4 py-2 bg-brand-600 text-white rounded-lg hover:bg-brand-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isLoading ? <Loader2 size={16} className="animate-spin" /> : <Search size={16} />}
              查询
            </button>
            <button
              onClick={handleReset}
              className="flex items-center gap-2 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
            >
              <X size={16} />
              重置
            </button>
          </div>
        </div>
      );
    }

    return (
      <div className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm text-gray-600 mb-1">开始时间</label>
            <input
              type="datetime-local"
              value={formatDateTimeForInput(filterValues.startTime)}
              onChange={(e) => {
                const val = e.target.value;
                if (val) {
                  setFilterValues(prev => ({ ...prev, startTime: val.replace('T', ' ') + ':00' }));
                }
              }}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-500 text-sm"
            />
          </div>
          <div>
            <label className="block text-sm text-gray-600 mb-1">结束时间</label>
            <input
              type="datetime-local"
              value={formatDateTimeForInput(filterValues.endTime)}
              onChange={(e) => {
                const val = e.target.value;
                if (val) {
                  setFilterValues(prev => ({ ...prev, endTime: val.replace('T', ' ') + ':00' }));
                }
              }}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-500 text-sm"
            />
          </div>
          <div>
            <label className="block text-sm text-gray-600 mb-1">返回行数</label>
            <input
              type="number"
              value={filterValues.limit}
              onChange={(e) => setFilterValues(prev => ({ ...prev, limit: Number(e.target.value) || 100 }))}
              min={1}
              max={1000}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-500 text-sm"
            />
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm text-gray-600 mb-1">最小 Tokens</label>
            <input
              type="number"
              value={filterValues.minTokens}
              onChange={(e) => setFilterValues(prev => ({ ...prev, minTokens: Number(e.target.value) || 0 }))}
              min={0}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-500 text-sm"
            />
          </div>
          <div>
            <label className="block text-sm text-gray-600 mb-1">最大 Tokens</label>
            <input
              type="number"
              value={filterValues.maxTokens}
              onChange={(e) => setFilterValues(prev => ({ ...prev, maxTokens: Number(e.target.value) || 1000000 }))}
              min={0}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-500 text-sm"
            />
          </div>
        </div>

        <div className="flex items-center gap-3">
          <button
            onClick={handleSearch}
            disabled={isLoading}
            className="flex items-center gap-2 px-4 py-2 bg-brand-600 text-white rounded-lg hover:bg-brand-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isLoading ? <Loader2 size={16} className="animate-spin" /> : <Search size={16} />}
            查询
          </button>
          <button
            onClick={handleReset}
            className="flex items-center gap-2 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
          >
            <X size={16} />
            重置
          </button>
        </div>
      </div>
    );
  };

  const renderRecordTable = () => {
    if (!hasSearched) {
      return (
        <div className="text-center py-12 text-gray-400">
          <AlertTriangle size={48} className="mx-auto mb-4 opacity-50" />
          <p>请设置筛选条件后点击查询</p>
        </div>
      );
    }

    if (isLoading) {
      return (
        <div className="text-center py-12">
          <Loader2 size={32} className="mx-auto mb-4 animate-spin text-brand-500" />
          <p className="text-gray-500">正在查询中...</p>
        </div>
      );
    }

    if (recordType === 'slideStats') {
      return renderSlideStatsTable();
    }

    if (records.length === 0) {
      return (
        <div className="text-center py-12 text-gray-400">
          <AlertTriangle size={48} className="mx-auto mb-4 opacity-50" />
          <p>未找到符合条件的风险记录</p>
        </div>
      );
    }

    return (
      <div className="overflow-x-auto">
        <div className="mb-4 text-sm text-gray-600">
          共找到 <span className="font-semibold text-brand-600">{total}</span> 条风险记录
        </div>
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-8"></th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-20">ID</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-28">用户</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-20">角色</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">内容预览</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-28">输入Tokens</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-28">输出Tokens</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-28">总Tokens</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-40">创建时间</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {records.map((record) => (
              <React.Fragment key={record.id}>
                <tr
                  className={`hover:bg-gray-50 cursor-pointer ${expandedId === record.id ? 'bg-gray-50' : ''}`}
                  onClick={() => toggleExpand(record.id)}
                >
                  <td className="px-4 py-3 whitespace-nowrap">
                    {expandedId === record.id ? (
                      <ChevronUp size={16} className="text-gray-400" />
                    ) : (
                      <ChevronDown size={16} className="text-gray-400" />
                    )}
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600">{record.id}</td>
                  <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-800 font-medium">{record.previous_user_message?.username || record.username}</td>
                  <td className="px-4 py-3 whitespace-nowrap">
                    <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${
                      record.role === 'assistant'
                        ? 'bg-purple-100 text-purple-700'
                        : 'bg-blue-100 text-blue-700'
                    }`}>
                      {record.role === 'assistant' ? 'AI' : '用户'}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-800 max-w-xs">
                    <div className="truncate" title={record.content}>
                      {record.content}
                    </div>
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600 font-mono">
                    {formatTokens(record.input_tokens)}
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600 font-mono">
                    {formatTokens(record.output_tokens)}
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap text-sm font-mono font-semibold text-red-600">
                    {formatTokens(record.tokens_total)}
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                    {formatDateTime(record.created_at)}
                  </td>
                </tr>
                {expandedId === record.id && (
                  <tr className="bg-gray-50">
                    <td colSpan={9} className="px-6 py-4">
                      <div className="space-y-4">
                        <div>
                          <h4 className="text-sm font-medium text-gray-700 mb-2">当前消息详情</h4>
                          <div className="bg-white rounded-lg p-4 border text-sm text-gray-800 whitespace-pre-wrap max-h-60 overflow-y-auto">
                            {record.content}
                          </div>
                          <div className="mt-2 grid grid-cols-4 gap-4 text-xs text-gray-500">
                            <div>Lesson ID: {record.lesson_id ?? '-'}</div>
                            <div>Artifact ID: {record.artifact_id ?? '-'}</div>
                            <div>输入 Tokens: {record.input_tokens.toLocaleString()}</div>
                            <div>输出 Tokens: {record.output_tokens.toLocaleString()}</div>
                          </div>
                        </div>
                        {record.previous_user_message && (
                          <div>
                            <h4 className="text-sm font-medium text-gray-700 mb-2">
                              前一条用户消息 (ID: {record.previous_user_message.id}, 用户: {record.previous_user_message.username})
                            </h4>
                            <div className="bg-blue-50 rounded-lg p-4 border border-blue-200 text-sm text-gray-800 whitespace-pre-wrap max-h-60 overflow-y-auto">
                              {record.previous_user_message.content}
                            </div>
                            <div className="mt-2 text-xs text-gray-500">
                              创建时间: {formatDateTime(record.previous_user_message.created_at)} |
                              输入 Tokens: {record.previous_user_message.input_tokens.toLocaleString()}
                            </div>
                          </div>
                        )}
                      </div>
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  const renderSlideStatsTable = () => {
    if (slideStatsRecords.length === 0) {
      return (
        <div className="text-center py-12 text-gray-400">
          <AlertTriangle size={48} className="mx-auto mb-4 opacity-50" />
          <p>未找到符合条件的记录</p>
        </div>
      );
    }

    return (
      <div className="overflow-x-auto">
        <div className="mb-4 text-sm text-gray-600">
          共找到 <span className="font-semibold text-brand-600">{total}</span> 条高消费Slide记录
        </div>
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-8"></th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-20">Slide ID</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-20">索引</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-24">用户</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">课程/阶段/课时</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-24">输入Tokens</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-24">输出Tokens</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-24">总Tokens</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-20">费用(¥)</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {slideStatsRecords.map((record) => (
              <React.Fragment key={record.slide_id}>
                <tr
                  className={`hover:bg-gray-50 cursor-pointer ${expandedId === record.slide_id ? 'bg-gray-50' : ''}`}
                  onClick={() => toggleExpand(record.slide_id)}
                >
                  <td className="px-4 py-3 whitespace-nowrap">
                    {expandedId === record.slide_id ? (
                      <ChevronUp size={16} className="text-gray-400" />
                    ) : (
                      <ChevronDown size={16} className="text-gray-400" />
                    )}
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600">{record.slide_id}</td>
                  <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600">{record.slide_index}</td>
                  <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-800 font-medium">{record.username}</td>
                  <td className="px-4 py-3 text-sm text-gray-800 max-w-xs">
                    <div className="truncate" title={`${record.course_title} / L${record.lesson_sort_order} ${record.lesson_title}`}>
                      {record.course_title} / L{record.lesson_sort_order} {record.lesson_title}
                    </div>
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600 font-mono">
                    {formatTokens(record.total_tokens_input)}
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600 font-mono">
                    {formatTokens(record.total_tokens_output)}
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap text-sm font-mono font-semibold text-red-600">
                    {formatTokens(record.total_tokens)}
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap text-sm font-mono font-semibold text-orange-600">
                    ¥{record.cost_rmb.toFixed(4)}
                  </td>
                </tr>
                {expandedId === record.slide_id && (
                  <tr className="bg-gray-50">
                    <td colSpan={9} className="px-6 py-4">
                      <div className="space-y-4">
                        <div>
                          <h4 className="text-sm font-medium text-gray-700 mb-2">Slide 信息</h4>
                          <div className="bg-white rounded-lg p-4 border text-sm text-gray-800">
                            <div className="grid grid-cols-3 gap-4">
                              <div><span className="text-gray-500">教案ID:</span> {record.plan_id}</div>
                              <div><span className="text-gray-500">版本:</span> {record.plan_version_number}</div>
                              <div><span className="text-gray-500">创建者ID:</span> {record.plan_created_by}</div>
                              <div><span className="text-gray-500">课程:</span> {record.course_title}</div>
                              <div><span className="text-gray-500">阶段:</span> {record.stage_title}</div>
                              <div><span className="text-gray-500">课时:</span> {record.lesson_title}</div>
                            </div>
                          </div>
                        </div>
                        {record.chat_details && record.chat_details.length > 0 && (
                          <div>
                            <h4 className="text-sm font-medium text-gray-700 mb-2">聊天记录明细 ({record.chat_details.length}条)</h4>
                            <div className="bg-white rounded-lg border max-h-80 overflow-y-auto">
                              <table className="min-w-full divide-y divide-gray-200">
                                <thead className="bg-gray-50 sticky top-0">
                                  <tr>
                                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">ID</th>
                                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">用户</th>
                                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">角色</th>
                                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">内容</th>
                                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">输入</th>
                                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">输出</th>
                                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">时间</th>
                                  </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-200">
                                  {record.chat_details.map((chat) => (
                                    <tr key={chat.id} className="hover:bg-gray-50">
                                      <td className="px-3 py-2 text-xs text-gray-600">{chat.id}</td>
                                      <td className="px-3 py-2 text-xs text-gray-800">{chat.username}</td>
                                      <td className="px-3 py-2">
                                        <span className={`inline-flex px-1.5 py-0.5 text-xs font-medium rounded-full ${
                                          chat.role === 'assistant'
                                            ? 'bg-purple-100 text-purple-700'
                                            : 'bg-blue-100 text-blue-700'
                                        }`}>
                                          {chat.role === 'assistant' ? 'AI' : '用户'}
                                        </span>
                                      </td>
                                      <td className="px-3 py-2 text-xs text-gray-800 max-w-xs">
                                        <div className="truncate" title={chat.content}>{chat.content}</div>
                                      </td>
                                      <td className="px-3 py-2 text-xs text-gray-600 font-mono">{formatTokens(chat.input_tokens)}</td>
                                      <td className="px-3 py-2 text-xs text-gray-600 font-mono">{formatTokens(chat.output_tokens)}</td>
                                      <td className="px-3 py-2 text-xs text-gray-500">{formatDateTime(chat.created_at)}</td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          </div>
                        )}
                      </div>
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  const getTitleByType = () => {
    switch (recordType) {
      case 'slide':
        return '课件开发风险记录';
      case 'plan':
        return '教案开发风险记录';
      case 'slideStats':
        return '高消费Slide列表';
      default:
        return '';
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-white border-b sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 py-4 flex justify-between items-center">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate('/admin')}
              className="flex items-center gap-2 px-3 py-2 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <ArrowLeft size={18} />
              返回
            </button>
            <div className="flex items-center gap-2">
              <AlertTriangle size={24} className="text-orange-500" />
              <h1 className="text-xl font-bold text-gray-800">用量预警查询</h1>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <LogoutButton />
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-6">
        <div className="bg-white rounded-lg shadow-sm border mb-6">
          <div className="border-b border-gray-200 px-4 py-3">
            <div className="flex items-center gap-4">
              <span className="text-sm font-medium text-gray-500">记录类型：</span>
              <div className="flex">
                <button
                  onClick={() => handleRecordTypeChange('slide')}
                  className={`px-4 py-2 text-sm font-medium transition-colors ${
                    recordType === 'slide'
                      ? 'text-white bg-brand-600 rounded-l-lg'
                      : 'text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-l-lg'
                  }`}
                >
                  课件开发记录
                </button>
                <button
                  onClick={() => handleRecordTypeChange('plan')}
                  className={`px-4 py-2 text-sm font-medium transition-colors ${
                    recordType === 'plan'
                      ? 'text-white bg-brand-600'
                      : 'text-gray-600 bg-gray-100 hover:bg-gray-200'
                  }`}
                >
                  教案开发记录
                </button>
                <button
                  onClick={() => handleRecordTypeChange('slideStats')}
                  className={`px-4 py-2 text-sm font-medium flex items-center gap-1 transition-colors ${
                    recordType === 'slideStats'
                      ? 'text-white bg-orange-500 rounded-r-lg'
                      : 'text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-r-lg'
                  }`}
                >
                  <DollarSign size={14} />
                  高消费Slide
                </button>
              </div>
            </div>
          </div>

          <div className="p-4">
            <div className="flex items-center gap-2 mb-4">
              <Filter size={18} className="text-gray-400" />
              <span className="font-medium text-gray-700">筛选条件</span>
            </div>
            {renderFilterForm()}
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border">
          <div className="px-4 py-3 border-b border-gray-200">
            <h3 className="font-medium text-gray-800">{getTitleByType()}</h3>
          </div>
          <div className="p-4">
            {renderRecordTable()}
          </div>
        </div>
      </main>
    </div>
  );
};

export default UsageWarningPage;
